import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { AnimatePresence, motion } from "framer-motion";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import {
  loginWithPin,
  registerWithPin,
  sendEmailCode,
  verifyEmailCode,
} from "../../services/auth";
import { setAccessToken } from "../../services/login";
import { TURNSTILE_ENABLED, TurnstileWidget } from "./TurnstileWidget";
import {
  getCurrentLanguage,
  setLanguage,
  SUPPORTED_LANGS,
  type SupportedLang,
} from "../../i18n";
import { saveLanguagePreference } from "../../services/me";
import { v } from "../../utils/runtime";

const LANG_OPTIONS: { code: SupportedLang; native: string; flag: string }[] = [
  { code: "ru", native: "Русский", flag: "circle-flags:ru" },
  { code: "en", native: "English", flag: "circle-flags:us" },
];

/**
 * Веб-версия входа: единый экран с 3-4 шагами.
 *
 *   Шаг «email»          — юзер вводит адрес, мы шлём код.
 *   Шаг «code»           — юзер вводит код из письма.
 *                          Дальше mode из бэка решает, куда идти:
 *   Шаг «pin_login»      — юзер уже был → ввод его PIN (совпадает с TG).
 *   Шаг «pin_register»   — юзер новый → PIN + подтверждение + опц. имя/фамилия.
 *
 * После успешного шага сохраняем access-token в localStorage и делаем
 * hard-reload на `/`, чтобы RootLayout перечитал `/me` и провёл юзера в
 * онбординг (для регистрации без TG у нас is_onboarded=true уже,
 * т.к. регистрация в вебе — сама по себе онбординг). PIN хешируется на
 * бэке, cookie рефреша ставится через Set-Cookie.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const authFieldSx = (theme: any) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    "& fieldset": {
      border: "1px solid !important",
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.15) !important"
          : "rgba(0,0,0,0.15) !important",
    },
    "&:hover fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.3) !important"
          : "rgba(0,0,0,0.3) !important",
    },
    "&.Mui-focused fieldset": {
      borderWidth: "1.5px !important",
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
});
const RESEND_COOLDOWN_SEC = 45;

type Step = "email" | "code" | "pin_login" | "pin_register";

interface ApiErrorBody {
  detail?: string;
  message?: string;
}

function getApiErrorDetail(err: unknown): { status?: number; detail?: string } {
  const e = err as AxiosError<ApiErrorBody>;
  return {
    status: e.response?.status,
    detail: e.response?.data?.detail ?? e.response?.data?.message,
  };
}

export function WebAuthScreen() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation(["web-auth", "common"]);
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string>("");
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState("");
  const [mode, setMode] = useState<"login" | "register">("login");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [remember, setRemember] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  // Cloudflare Turnstile: если фича не включена, `TURNSTILE_ENABLED` = false
  // и валидация капчи пропускается — на кнопку это тогда не влияет.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [registerPhase, setRegisterPhase] = useState<"pin" | "confirm" | "profile">("pin");
  const [langOpen, setLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<SupportedLang>(getCurrentLanguage());

  // Реф-код из query string (для новых регистраций через инвайт-ссылку).
  const referralCode = useMemo(() => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get("ref")?.trim() || undefined;
    } catch {
      return undefined;
    }
  }, []);

  // Countdown для «отправить ещё раз».
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // -------- mutations --------

  const sendCodeMut = useMutation({
    mutationFn: () =>
      sendEmailCode(
        email.trim().toLowerCase(),
        "auto",
        turnstileToken ?? undefined,
      ),
    onSuccess: (data) => {
      setMode(data.mode);
      setMaskedEmail(data.masked_email);
      setResendCooldown(RESEND_COOLDOWN_SEC);
      setStep("code");
    },
    onError: (err) => {
      const { status, detail } = getApiErrorDetail(err);
      if (status === 404) {
        enqueueSnackbar(t("web-auth:errors.user_not_found"), {
          variant: "error",
        });
      } else if (status === 409) {
        enqueueSnackbar(t("web-auth:errors.user_exists"), { variant: "error" });
      } else {
        enqueueSnackbar(detail || t("web-auth:errors.generic"), {
          variant: "error",
        });
      }
    },
  });

  const verifyCodeMut = useMutation({
    mutationFn: () => verifyEmailCode(email.trim().toLowerCase(), code),
    onSuccess: (data) => {
      setTicket(data.ticket);
      setMode(data.mode);
      setStep(data.mode === "login" ? "pin_login" : "pin_register");
    },
    onError: (err) => {
      const { status, detail } = getApiErrorDetail(err);
      if (status === 401) {
        enqueueSnackbar(detail || t("web-auth:errors.wrong_code"), {
          variant: "error",
        });
      } else {
        enqueueSnackbar(t("web-auth:errors.generic"), { variant: "error" });
      }
    },
  });

  const loginMut = useMutation({
    mutationFn: () => loginWithPin(ticket, pin, remember),
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      // Полный reload — чтобы RootLayout перечитал /me и снял чекбоксы.
      window.location.replace("/");
    },
    onError: (err) => {
      const { status, detail } = getApiErrorDetail(err);
      if (status === 401) {
        enqueueSnackbar(detail || t("web-auth:errors.wrong_pin"), {
          variant: "error",
        });
        setPin("");
      } else {
        enqueueSnackbar(detail || t("web-auth:errors.generic"), {
          variant: "error",
        });
      }
    },
  });

  const registerMut = useMutation({
    mutationFn: () =>
      registerWithPin({
        ticket,
        pin,
        pinConfirm,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        remember30d: remember,
        referralCode,
      }),
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      window.location.replace("/");
    },
    onError: (err) => {
      const { detail } = getApiErrorDetail(err);
      enqueueSnackbar(detail || t("web-auth:errors.generic"), {
        variant: "error",
      });
    },
  });

  // -------- handlers --------

  const handleEmailSubmit = () => {
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setEmailError(t("web-auth:errors.invalid_email") as string);
      return;
    }
    setEmailError(null);
    sendCodeMut.mutate();
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    sendCodeMut.mutate();
  };

  const handleCodeSubmit = () => {
    if (code.length !== 6) return;
    verifyCodeMut.mutate();
  };

  const handlePinLogin = useCallback(() => {
    loginMut.mutate();
  }, [loginMut]);

  const handleRegister = () => {
    if (pin.length !== 4 || pinConfirm.length !== 4) return;
    if (pin !== pinConfirm) {
      enqueueSnackbar(t("web-auth:errors.pin_mismatch"), { variant: "error" });
      return;
    }
    registerMut.mutate();
  };

  const isBusy =
    sendCodeMut.isLoading ||
    verifyCodeMut.isLoading ||
    loginMut.isLoading ||
    registerMut.isLoading;

  useEffect(() => {
    if (step === "pin_login" && pin.length === 4 && !loginMut.isLoading) {
      const timer = setTimeout(() => loginMut.mutate(), 180);
      return () => clearTimeout(timer);
    }
  }, [pin, step, loginMut]);

  // -------- header (persistent, animated icon) --------

  const headerTitle = useMemo(() => {
    switch (step) {
      case "email":
        return t("web-auth:login.title");
      case "code":
        return t("web-auth:code.title");
      case "pin_login":
        return t("web-auth:pin_login.title");
      case "pin_register":
        if (registerPhase === "confirm") return t("web-auth:pin_register.confirm_title");
        if (registerPhase === "profile") return t("web-auth:pin_register.profile_title");
        return t("web-auth:pin_register.title");
    }
  }, [step, t, registerPhase]);

  const headerSubtitle = useMemo(() => {
    switch (step) {
      case "email":
        return t("web-auth:login.subtitle");
      case "code":
        return `${t("web-auth:code.subtitle_prefix")} ${maskedEmail}`;
      case "pin_login":
        return t("web-auth:pin_login.subtitle");
      case "pin_register":
        if (registerPhase === "confirm") return t("web-auth:pin_register.confirm_subtitle");
        if (registerPhase === "profile") return t("web-auth:pin_register.profile_subtitle");
        return t("web-auth:pin_register.subtitle");
    }
  }, [step, t, maskedEmail, registerPhase]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        // Плавный градиент под контентом — как на /home
        background: isDark
          ? "radial-gradient(1200px 800px at 50% -10%, rgba(48,112,241,0.18), transparent 60%), #0F0F12"
          : "radial-gradient(1200px 800px at 50% -10%, rgba(48,112,241,0.10), transparent 60%), #F5F5F7",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          position: "relative",
          bgcolor: isDark ? "rgba(23,23,27,0.72)" : "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
          boxShadow: isDark
            ? "0 24px 60px rgba(0,0,0,0.5)"
            : "0 12px 40px rgba(15,15,18,0.08)",
        }}
      >
        {/* Кнопка смены языка — правый верхний угол */}
        <IconButton
          onClick={() => setLangOpen(true)}
          size="small"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <Icon
            icon={LANG_OPTIONS.find((o) => o.code === currentLang)?.flag || "circle-flags:ru"}
            width={22}
            height={22}
          />
        </IconButton>

        <Stack alignItems="center" spacing={1.5} sx={{ mb: 3.5 }}>
          <Box
            component="img"
            src={isDark ? v("/alfin-logo.svg") : v("/alfin-logo-l.svg")}
            alt="Zero Wallet"
            sx={{ height: 28, mb: 1 }}
          />

          <AnimatePresence mode="wait">
            <motion.div
              key={`${headerTitle}-${headerSubtitle}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={{ textAlign: "center" }}
            >
              <Typography variant="h6" fontWeight={700}>
                {headerTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {headerSubtitle}
              </Typography>
            </motion.div>
          </AnimatePresence>
        </Stack>

        {/* Steps */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
          >
            {step === "email" && (
              <EmailStep
                email={email}
                setEmail={(v) => {
                  setEmail(v);
                  setEmailError(null);
                }}
                error={emailError}
                onSubmit={handleEmailSubmit}
                loading={sendCodeMut.isLoading}
                turnstileToken={turnstileToken}
                onTurnstileVerify={setTurnstileToken}
                onTurnstileExpire={() => setTurnstileToken(null)}
              />
            )}
            {step === "code" && (
              <CodeStep
                code={code}
                setCode={setCode}
                onSubmit={handleCodeSubmit}
                onResend={handleResend}
                onWrongEmail={() => {
                  setStep("email");
                  setCode("");
                }}
                cooldown={resendCooldown}
                loading={verifyCodeMut.isLoading}
                resending={sendCodeMut.isLoading}
              />
            )}
            {step === "pin_login" && (
              <PinStep
                pin={pin}
                setPin={setPin}
                remember={remember}
                setRemember={setRemember}
                onSubmit={handlePinLogin}
                loading={loginMut.isLoading}
                cta={t("web-auth:pin_login.cta") as string}
                rememberLabel={t("web-auth:pin_login.remember_30d") as string}
              />
            )}
            {step === "pin_register" && (
              <PinRegisterStep
                pin={pin}
                setPin={setPin}
                pinConfirm={pinConfirm}
                setPinConfirm={setPinConfirm}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                remember={remember}
                setRemember={setRemember}
                onSubmit={handleRegister}
                loading={registerMut.isLoading}
                hasReferral={Boolean(referralCode)}
                onPhaseChange={setRegisterPhase}
              />
            )}
          </motion.div>
        </AnimatePresence>

      </Box>

      {/* Модальное окно выбора языка */}
      <Drawer
        anchor="bottom"
        open={langOpen}
        onClose={() => setLangOpen(false)}
        sx={{ zIndex: 100010 }}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
        }}
        PaperProps={{
          sx: {
            bgcolor: "secondary.main",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            padding: "10px 16px 28px",
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            width: "44px",
            height: "4px",
            borderRadius: "4px",
            bgcolor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
            margin: "0 auto 14px",
          }}
        />
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            textAlign: "center",
            mb: 2,
            color: "text.primary",
          }}
        >
          {t("common:actions.select")}
        </Typography>
        <Box sx={{ display: "grid", gap: "8px" }}>
          {SUPPORTED_LANGS.map((code) => {
            const opt = LANG_OPTIONS.find((o) => o.code === code);
            if (!opt) return null;
            const active = opt.code === currentLang;
            return (
              <Box
                key={opt.code}
                onClick={() => {
                  setCurrentLang(opt.code);
                  setLanguage(opt.code);
                  setLangOpen(false);
                  void saveLanguagePreference(opt.code);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px",
                  borderRadius: "16px",
                  cursor: "pointer",
                  bgcolor: active
                    ? isDark
                      ? "rgba(48,112,241,0.14)"
                      : "rgba(48,112,241,0.10)"
                    : isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.04)",
                  border: active
                    ? `1px solid ${theme.palette.primary.main}`
                    : "1px solid transparent",
                  transition: "background 0.15s, border-color 0.15s",
                  "&:active": {
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon icon={opt.flag} width={32} height={32} />
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "15px",
                    fontWeight: active ? 600 : 500,
                    color: "text.primary",
                  }}
                >
                  {opt.native}
                </Typography>
                {active && (
                  <Icon
                    icon="solar:check-circle-bold"
                    width={22}
                    height={22}
                    color={theme.palette.primary.main}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Drawer>
    </Box>
  );
}

// ---------------- Step: email ----------------

function EmailStep(props: {
  email: string;
  setEmail: (v: string) => void;
  error: string | null;
  onSubmit: () => void;
  loading: boolean;
  turnstileToken: string | null;
  onTurnstileVerify: (token: string) => void;
  onTurnstileExpire: () => void;
}) {
  const { t } = useTranslation("web-auth");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Кнопка блокируется, если Turnstile активна, а токена ещё нет.
  const needsTurnstile = TURNSTILE_ENABLED && !props.turnstileToken;

  return (
    <Stack spacing={2}>
      <TextField
        inputRef={inputRef}
        label={t("login.email_label")}
        placeholder={t("login.email_placeholder") as string}
        value={props.email}
        onChange={(e) => props.setEmail(e.target.value)}
        error={Boolean(props.error)}
        helperText={props.error ?? " "}
        autoComplete="email"
        inputMode="email"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !needsTurnstile) props.onSubmit();
        }}
        fullWidth
        sx={authFieldSx}
      />
      {TURNSTILE_ENABLED && (
        <TurnstileWidget
          onVerify={props.onTurnstileVerify}
          onExpire={props.onTurnstileExpire}
          onError={props.onTurnstileExpire}
        />
      )}
      <Button
        variant="contained"
        size="large"
        onClick={props.onSubmit}
        disabled={
          props.loading || props.email.trim().length === 0 || needsTurnstile
        }
        fullWidth
        sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600, py: 1.4 }}
      >
        {t("login.cta")}
      </Button>
    </Stack>
  );
}

// ---------------- Step: code ----------------

function CodeStep(props: {
  code: string;
  setCode: (v: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onWrongEmail: () => void;
  cooldown: number;
  loading: boolean;
  resending: boolean;
}) {
  const { t } = useTranslation("web-auth");
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Stack spacing={2}>
      <TextField
        inputRef={inputRef}
        value={props.code}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
          props.setCode(digits);
          if (digits.length === 6) props.onSubmit();
        }}
        placeholder={t("code.code_label") as string}
        inputProps={{
          inputMode: "numeric",
          maxLength: 6,
          style: {
            letterSpacing: 8,
            fontSize: 22,
            textAlign: "center",
            fontWeight: 600,
            padding: "16px 14px",
          },
        }}
        autoComplete="one-time-code"
        fullWidth
        sx={authFieldSx}
      />
      <Button
        variant="contained"
        size="large"
        onClick={props.onSubmit}
        disabled={props.loading || props.code.length !== 6}
        fullWidth
        sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600, py: 1.4 }}
      >
        {t("code.cta")}
      </Button>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 0.5,
          rowGap: 0,
        }}
      >
        <Button
          onClick={props.onResend}
          disabled={props.cooldown > 0 || props.resending}
          size="small"
          sx={{ textTransform: "none", fontSize: 13, whiteSpace: "nowrap" }}
        >
          {props.cooldown > 0
            ? t("code.resend_wait", { seconds: props.cooldown })
            : t("code.resend")}
        </Button>
        <Button
          onClick={props.onWrongEmail}
          size="small"
          sx={{ textTransform: "none", fontSize: 13, whiteSpace: "nowrap" }}
        >
          {t("code.wrong_email")}
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------- Step: PIN (login) ----------------

function PinDigits({ length, value }: { length: number; value: string }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ my: 1.5 }}>
      {Array.from({ length }).map((_, i) => {
        const filled = i < value.length;
        return (
          <Box
            key={i}
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: filled ? theme.palette.primary.main : "transparent",
              border: `2px solid ${
                filled ? theme.palette.primary.main : theme.palette.divider
              }`,
              transition: "all 0.2s",
              transform: filled ? "scale(1.1)" : "scale(1)",
            }}
          />
        );
      })}
    </Stack>
  );
}

function PinKeypad(props: {
  onDigit: (d: string) => void;
  onBackspace: () => void;
}) {
  const theme = useTheme();
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 1.5,
        mt: 2,
        maxWidth: 280,
        mx: "auto",
        width: "100%",
      }}
    >
      {keys.map((k, i) => {
        if (k === "") return <Box key={i} />;
        return (
          <IconButton
            key={i}
            onClick={() => (k === "back" ? props.onBackspace() : props.onDigit(k))}
            sx={{
              width: "100%",
              height: 56,
              borderRadius: 3,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(0,0,0,0.03)",
              fontSize: 22,
              fontWeight: 600,
              color: theme.palette.text.primary,
              "&:hover": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
              },
            }}
          >
            {k === "back" ? (
              <Icon icon="solar:backspace-linear" width={22} height={22} />
            ) : (
              k
            )}
          </IconButton>
        );
      })}
    </Box>
  );
}

function PinStep(props: {
  pin: string;
  setPin: (v: string) => void;
  remember: boolean;
  setRemember: (v: boolean) => void;
  onSubmit: () => void;
  loading: boolean;
  cta: string;
  rememberLabel: string;
}) {
  const handleDigit = useCallback(
    (d: string) => {
      if (props.pin.length >= 4) return;
      props.setPin(props.pin + d);
    },
    [props],
  );

  return (
    <Stack alignItems="center" spacing={1}>
      <PinDigits length={4} value={props.pin} />
      <PinKeypad
        onDigit={handleDigit}
        onBackspace={() => props.setPin(props.pin.slice(0, -1))}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={props.remember}
            onChange={(e) => props.setRemember(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            {props.rememberLabel}
          </Typography>
        }
        sx={{ mt: 1 }}
      />
      <Button
        variant="text"
        size="small"
        disabled={props.pin.length !== 4 || props.loading}
        onClick={props.onSubmit}
        sx={{ textTransform: "none", fontSize: 13 }}
      >
        {props.cta}
      </Button>
    </Stack>
  );
}

// ---------------- Step: register PIN ----------------

function PinRegisterStep(props: {
  pin: string;
  setPin: (v: string) => void;
  pinConfirm: string;
  setPinConfirm: (v: string) => void;
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  remember: boolean;
  setRemember: (v: boolean) => void;
  onSubmit: () => void;
  loading: boolean;
  hasReferral: boolean;
  onPhaseChange: (phase: "pin" | "confirm" | "profile") => void;
}) {
  const { t } = useTranslation("web-auth");
  const { enqueueSnackbar } = useSnackbar();
  const [phase, setPhaseLocal] = useState<"pin" | "confirm" | "profile">("pin");

  const setPhase = useCallback(
    (p: "pin" | "confirm" | "profile") => {
      setPhaseLocal(p);
      props.onPhaseChange(p);
    },
    [props.onPhaseChange],
  );

  const handleDigit = (d: string) => {
    if (phase === "pin") {
      if (props.pin.length >= 4) return;
      const next = props.pin + d;
      props.setPin(next);
      if (next.length === 4) setTimeout(() => setPhase("confirm"), 180);
    } else if (phase === "confirm") {
      if (props.pinConfirm.length >= 4) return;
      const next = props.pinConfirm + d;
      props.setPinConfirm(next);
      if (next.length === 4) {
        setTimeout(() => {
          if (next !== props.pin) {
            enqueueSnackbar(t("errors.pin_mismatch"), { variant: "error" });
            props.setPinConfirm("");
            props.setPin("");
            setPhase("pin");
          } else {
            setPhase("profile");
          }
        }, 180);
      }
    }
  };
  const handleBackspace = () => {
    if (phase === "pin") props.setPin(props.pin.slice(0, -1));
    else if (phase === "confirm") props.setPinConfirm(props.pinConfirm.slice(0, -1));
  };

  if (phase === "profile") {
    return (
      <Stack spacing={2}>
        <TextField
          label={t("pin_register.first_name_label")}
          value={props.firstName}
          onChange={(e) => props.setFirstName(e.target.value)}
          fullWidth
          required
          sx={authFieldSx}
        />
        <TextField
          label={t("pin_register.last_name_label")}
          value={props.lastName}
          onChange={(e) => props.setLastName(e.target.value)}
          fullWidth
          sx={authFieldSx}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={props.remember}
              onChange={(e) => props.setRemember(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              {t("pin_register.remember_30d")}
            </Typography>
          }
        />
        {props.hasReferral && (
          <Typography
            variant="caption"
            color="primary.main"
            sx={{ textAlign: "center", display: "block" }}
          >
            {t("pin_register.referral_note")}
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          onClick={props.onSubmit}
          disabled={props.loading || !props.firstName.trim()}
          fullWidth
          sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600, py: 1.4 }}
        >
          {t("pin_register.cta")}
        </Button>
      </Stack>
    );
  }

  const activePin = phase === "pin" ? props.pin : props.pinConfirm;

  return (
    <Stack alignItems="center" spacing={1}>
      <PinDigits length={4} value={activePin} />
      <PinKeypad onDigit={handleDigit} onBackspace={handleBackspace} />
    </Stack>
  );
}
